// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuPin } from "react-icons/lu";
import { FiExternalLink } from "react-icons/fi";
import { PiDownloadSimpleBold } from "react-icons/pi";
import RailTip from "./RailTip";
import CustomContextMenu2 from "./CustomContextMenu2";
import { GridIcon, LockKeyholeIcon } from "../icons";
import { handleItem } from "../../utils/handleItem";
import { getSession } from "../../utils/sessionstore";
import { logAmplitudeEvent } from "../../utils/amplitude/functions";
import amplitude from "../../utils/amplitude/initiate";
import { useToggleFavourite } from "../../hooks/useToggleFavourite";

// A single row in the sidebar Recents list: grid icon, label, pin toggle.
// Kept separate from NewDashboardBox, which already branches on `view` and has
// no pin affordance.
const RecentDashboardBox = ({ dashboard, afterlandDashboardNew }) => {
  const { clientID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const parentref = useRef(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [rcPosition, setRcPosition] = useState({});

  const { subscriptions, userDetails, clientname, activedashboard, isWebinarPopActive } =
    useSelector((state) => ({
      subscriptions: state.subscriptions,
      userDetails: state.userDetails,
      clientname: state.clientname,
      activedashboard: state.activedashboard,
      isWebinarPopActive: state.isWebinarPopActive,
    }));

  const { isFavourite, atCap, isBusy, toggleFavourite } = useToggleFavourite(dashboard);

  const isActive = activedashboard?._id === dashboard?._id;
  const isGreyed = dashboard?.url?.trim() === "Greyed";

  function resolveProductLine() {
    const targetId = dashboard?.standarddashboardId?._id;
    if (!subscriptions?.length || !targetId) return "";

    for (const subscription of subscriptions) {
      const categories = subscription.productcategoryIds || [];
      for (const category of categories) {
        const dashboards = category.dashboardIds || [];
        const match = dashboards.find(
          (dash) => dash.standarddashboardId?._id === targetId
        );
        if (match) return subscription.label;
      }
    }
    return "";
  }

  async function openDashboard(triggerType) {
    setContextMenuVisible(false);
    await handleItem({
      productLine: resolveProductLine(),
      clientID: clientID ?? getSession("client_id"),
      userDetails: userDetails,
      dashboard: dashboard,
      clientName: clientname,
      triggerType: triggerType,
      pathname: location.pathname,
      postItemHandle: afterlandDashboardNew,
      dispatch: dispatch,
      navigate: navigate,
      staticPop: isWebinarPopActive,
    });
  }

  function handleContextMenu(e) {
    setRcPosition({ x: e.clientX, y: e.clientY });
    e.preventDefault();
    setContextMenuVisible(true);
  }

  function togglePin(e) {
    if (atCap) {
      e.stopPropagation();
      return;
    }
    // Skip in-flight repeats: the hook ignores them, and isFavourite has already flipped.
    if (!isBusy) {
      logAmplitudeEvent(amplitude, isFavourite ? "Unpin Dashboard" : "Pin Dashboard", {
        productLine: resolveProductLine(),
        dashboardName: dashboard?.label,
        standardDashboardName: dashboard?.standarddashboardId?.standardName,
        dashboardId: dashboard?._id,
        clientName: clientname,
        clientId: clientID ?? getSession("client_id"),
        eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
      });
    }
    toggleFavourite(e);
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (parentref.current && !parentref.current.contains(event.target)) {
        setContextMenuVisible(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("contextmenu", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("contextmenu", handleClickOutside);
    };
  }, []);

  const pinTitle = atCap
    ? "Unpin a dashboard to pin this one"
    : isFavourite
    ? "Unpin"
    : "Pin";

  return (
    <div
      ref={parentref}
      onMouseLeave={() => setContextMenuVisible(false)}
      onContextMenu={handleContextMenu}
      className={`group font-geist h-8 shrink-0 w-[calc(100%-4px)] mx-auto px-3 rounded-md ani flex justify-start items-center gap-2 ${
        isActive ? "bg-general-secondary" : "hover:bg-general-accent"
      }`}
    >
      {contextMenuVisible &&
        dashboard?.type !== "video" &&
        dashboard?.url !== "Disabled" &&
        dashboard?.url !== "Greyed" && (
          <div>
            <CustomContextMenu2
              position={rcPosition}
              label="Open in New Tab"
              func={() => openDashboard("NEWTAB_SIDEBAR_DASHBOARD")}
            />
          </div>
        )}

      <GridIcon className="size-4 shrink-0 text-general-muted-foreground" />

      <div
        onClick={() => openDashboard("RECENT_DASHBOARD")}
        className={`flex-1 min-w-0 flex justify-start items-center gap-1.5 ${
          isGreyed
            ? "text-general-muted-foreground cursor-default"
            : "text-general-foreground cursor-pointer"
        }`}
      >
        <span className="flex-1 min-w-0 truncate text-left text-sm font-normal leading-5">
          {dashboard?.label}
        </span>
      </div>

      {dashboard?.type?.trim() === "Open in new tab" && (
        <span className="size-4 shrink-0 relative overflow-hidden text-general-muted-foreground hover:text-general-foreground ani">
          <FiExternalLink className="size-3.5 left-[1px] top-[1px] absolute" />
        </span>
      )}

      {dashboard?.type?.trim() === "Download" && (
        <PiDownloadSimpleBold className="size-3.5 shrink-0 text-general-muted-foreground" />
      )}

      {dashboard?.url?.trim() === "Disabled" && (
        <LockKeyholeIcon className="size-3.5 shrink-0 text-[#e07575]" />
      )}

      <RailTip title={pinTitle}>
        <button
          type="button"
          aria-disabled={atCap}
          onClick={togglePin}
          className={`flex size-4 shrink-0 items-center justify-center ani ${
            atCap ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
        >
          <LuPin
            className={`size-3.5 ani ${
              isFavourite
                ? "text-amber-500"
                : "text-general-muted-foreground opacity-0 group-hover:opacity-100"
            }`}
          />
        </button>
      </RailTip>
    </div>
  );
};

export default RecentDashboardBox;
